import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { selectIsRoleFlow, selectSkillName, selectTargetRole } from '../../../redux/skillSlice'
import {
  selectTotalScore,
  selectUserLevel,
  selectSubskillPerformance,
  selectCorrectCount,
  selectWrongCount,
  setIsAnalysisPageVisited
} from '../../../redux/newAnalysisSlice'
import ProgressBar from '../../../components/ProgressBar'
import SkillRadarChart from '../../../components/SkillRadarChart'
import { selectUserInfo } from '../../../redux/userInfoSlice'
import { TrendingUp } from 'lucide-react'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { capitalise } from '../../../utilities/constants'
import { Mixpanel } from '../../../config/Mixpanel'
import constants from '../../../utilities/constants'

// Course personalization section images
const imgFrame427323072 = "https://www.figma.com/api/mcp/asset/a7c98719-b004-4830-80cd-1bc57b7b418b"

const Analysis = () => {
  const navigate = useNavigate()

  // Get data from Redux
  const skillName = useSelector(selectSkillName) || 'Product Management'
  const userInfo = useSelector(selectUserInfo);
  const userName = userInfo?.name || ''

  // Get analysis data from Redux
  const totalScore = useSelector(selectTotalScore) || 0
  const userLevel = useSelector(selectUserLevel) || 'beginner'
  const subskillPerformance = useSelector(selectSubskillPerformance) || []
  const correctCount = useSelector(selectCorrectCount) || 0
  const wrongCount = useSelector(selectWrongCount) || 0
  const targetRole = useSelector(selectTargetRole) || ''
  const isRoleFlow = useSelector(selectIsRoleFlow);
  const dispatch = useDispatch();

  // Helper function to get level display name and color

  useEffect(() => {
    dispatch(setIsAnalysisPageVisited(true))

    // Track page load
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_ANALYSIS_PAGE_LOADED, {
      skill: skillName,
      totalScore: totalScore,
      userLevel: userLevel,
      correctCount: correctCount,
      wrongCount: wrongCount,
      targetRole: targetRole,
      session_id: userInfo?.session_id
    })
  }, [dispatch, skillName, totalScore, userLevel, correctCount, wrongCount, targetRole, userInfo?.session_id])

  const getLevelInfo = (level) => {
    switch (level.toLowerCase()) {
      case 'beginner':
        return { name: 'Beginner', color: '#fbbc05' }
      case 'intermediate':
        return { name: 'Intermediate', color: '#4CAF50' }
      case 'advanced':
        return { name: 'Advanced', color: '#406AFF' }
      default:
        return { name: 'Beginner', color: '#fbbc05' }
    }
  }

  const levelInfo = getLevelInfo(userLevel)

  // Calculate score percentage for progress circle
  const scorePercentage = Math.max(0, Math.min(100, totalScore))
  const circumference = 226 // 2 * Math.PI * 36
  const strokeDasharray = `${(scorePercentage / 100) * circumference} ${circumference}`

  const handleViewCourse = () => {
    // Track button click
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_ANALYSIS_VIEW_COURSE_CLICKED, {
      skill: skillName,
      totalScore: totalScore,
      userLevel: userLevel,
      session_id: userInfo?.session_id
    })

    navigate('/onboarding/loader-4') // Navigate to pricing page
  }

  return (
    <div className="w-full md:max-w-[45rem] flex-1 justify-center items-center mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative flex flex-col">
      <div className="mx-auto">
        {/* Progress Bar */}
        <div className="mt-6">
          <ProgressBar
            currentStep={isRoleFlow ? 7 : 5}
            totalSteps={isRoleFlow ? 8 : 6}
            stepText={`Step ${isRoleFlow ? 7 : 5}/${isRoleFlow ? 8 : 6} to  ${targetRole ? 'becoming ' + targetRole : 'mastering ' + capitalise(skillName)}`}
          />
        </div>

        <div className="mt-6 flex flex-col gap-6">
          {/* Step indicator */}


          <div className='mt-6 flex flex-col p-[2px] rounded-[1.5rem]' style={{
            backgroundImage: `linear-gradient(
          to right,
          rgba(64, 106, 255, 0.3) 0%,
          rgba(64, 106, 255, 1) 56%,
          rgba(64, 106, 255, 0.3) 100%
        )`
          }}>
            {/* Main content card */}
            <div className='flex flex-col p-6 rounded-[1.5rem] shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] w-full relative overflow-hidden' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>
              {/* Background decorations */}
              <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px]"
                style={{ backgroundImage: `url('https://img.learntube.ai/public-assets/personalise/web.webp')` }} />

              <div className="relative z-10">
                {/* Title Section */}
                <div className="mb-6">
                  <h1 className="text-[24px] font-bold bg-gradient-to-r from-white to-[#ffe394] bg-clip-text text-transparent leading-tight mb-2">
                    {userName}, Here's Where You Stand In {skillName}
                  </h1>
                  <p className="text-[12px] text-white/50 leading-relaxed">
                    We've analysed your performance in {skillName}. You have a solid foundation, improving a
                    few areas will move you much closer to your goal.
                  </p>
                </div>

                {/* Score Section */}
                <div className="flex flex-col items-center gap-4 mb-6">
                  <p className="w-full text-xs text-left text-[#70aeff] font-bold uppercase tracking-widest">
                    Overall Score
                  </p>

                  <div className="flex gap-3 items-center w-full">
                    {/* Circular Score */}
                    <div className="relative w-20 h-20 flex-shrink-0">
                      {/* Background circle */}
                      <div className="absolute inset-0 w-20 h-20 rounded-full border-4 border-[#406AFF]/15"></div>

                      {/* Progress circle */}
                      <div className="absolute inset-0 w-20 h-20">
                        <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 80 80">
                          <circle
                            cx="40"
                            cy="40"
                            r="36"
                            stroke={levelInfo.color}
                            strokeWidth="4"
                            fill="none"
                            strokeLinecap="round"
                            strokeDasharray={strokeDasharray}
                            className="drop-shadow-[0_0_8px_rgba(251,188,5,0.5)]"
                            style={{
                              filter: `drop-shadow(0 0 8px ${levelInfo.color}50)`
                            }}
                          />
                        </svg>
                      </div>

                      {/* Score text */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <span className="text-2xl font-bold drop-shadow-[0_0_4px_rgba(64,106,255,0.8)]" style={{ color: levelInfo.color }}>
                          {totalScore}
                        </span>
                        <span className="text-xs text-white/30">/100</span>
                      </div>
                    </div>

                    {/* Level Info */}
                    <div className="w-full flex-1 flex flex-col gap-2">
                      <div className="px-3 py-2 rounded-2xl inline-flex w-fit" style={{ backgroundColor: `${levelInfo.color}1A` }}>
                        <span className="text-xs font-medium w-fit" style={{ color: levelInfo.color }}>
                          Level: {levelInfo.name}
                        </span>
                      </div>

                      <div className="w-full flex gap-1 items-start">
                        <TrendingUp alt="" className="w-4 h-4 flex-shrink-0 text-[#54B226]" />
                        <div className="w-full flex flex-col gap-1 flex-1">
                          <span className="text-xs text-white">Aman • Product Manager @<img alt="" className="w-4 h-4 inline-block shrink-0 ml-1" src={'https://img.learntube.ai/public-assets/personalise/Frame%2023330.webp'} /></span>
                          <p className="text-xs italic text-white/50">
                            Started from scratch few months ago
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Skill Radar Chart */}
          <SkillRadarChart subskillData={subskillPerformance} />

          {/* How Your Course Will Be Section */}
          <div className="flex flex-col gap-4 w-full">
            <div className="flex flex-col">
              <h2 className="text-[20px] font-bold bg-gradient-to-r from-white to-[#ffe394] bg-clip-text text-transparent capitalize tracking-[0.2px]">
                How Your Course Will Be
              </h2>
            </div>

            <div className="bg-[rgba(64,106,255,0.1)] flex flex-col gap-6 p-4 rounded-3xl w-full">
              <div className="flex flex-col gap-4 w-full">
                {/* Feature list */}
                <div className="flex gap-3 items-center rounded-xl w-full">
                  <div className="w-4 h-4 flex-shrink-0">
                    <img alt="" className="w-full h-full" src={'https://img.learntube.ai/public-assets/personalise/Frame%20427323036.webp'} />
                  </div>
                  <div className="flex-1 text-xs text-white/70 leading-[1.4]">
                    Move you ahead in {correctCount} skills where you're already strong
                  </div>
                </div>

                <div className="flex gap-3 items-center rounded-xl w-full">
                  <div className="w-4 h-4 flex-shrink-0">
                    <img alt="" className="w-full h-full" src={'https://img.learntube.ai/public-assets/personalise/Frame%20427323037.webp'} />
                  </div>
                  <div className="flex-1 text-xs text-white/70 leading-[1.4]">
                    Skip the basics of what you already know
                  </div>
                </div>

                <div className="flex gap-3 items-center rounded-xl w-full">
                  <div className="w-4 h-4 flex-shrink-0">
                    <img alt="" className="w-full h-full" src={'https://img.learntube.ai/public-assets/personalise/Frame%20427323036.webp'} />
                  </div>
                  <div className="flex-1 text-xs text-white/70 leading-[1.4]">
                    Deep focus on your {wrongCount} key gaps - {subskillPerformance
                      ?.filter(skill => !skill.is_correct)
                      .map(skill => skill.subskill.split(' ').slice(0, 2).join(' '))
                      .join(', ')
                    }
                  </div>
                </div>
              </div>

              {/* CTA Section */}
              <div className="flex flex-col gap-6 px-3 py-4 rounded-xl relative overflow-hidden">
                {/* Background with overlay */}
                <div className="absolute inset-0 pointer-events-none rounded-xl">
                  <img alt="" className="absolute max-w-none object-cover opacity-50 rounded-xl w-full h-full" src={'https://img.learntube.ai/public-assets/personalise/web.webp'} />
                  <div className="absolute bg-gradient-to-b from-[rgba(0,0,0,0.2)] to-[rgba(64,106,255,0.2)] inset-0 rounded-xl" />
                </div>

                <div className="relative z-10 flex flex-col gap-3 items-center w-full">
                  <div className="backdrop-blur-[2px] bg-[rgba(64,106,255,0.15)] border border-[#406aff] flex items-center justify-center p-3 rounded-3xl">
                    <div className="bg-gradient-to-r from-white to-[#406aff] bg-clip-text text-base font-semibold text-transparent">
                      Ready for next step?
                    </div>
                  </div>
                  <div className="text-sm text-white/50 text-center leading-[1.4]">
                    We'll focus only on what helps you reach your goal 3x faster
                  </div>
                </div>

                <div className="relative z-10 flex flex-col gap-3 items-center justify-center w-full">
                  <button
                    onClick={handleViewCourse}
                    className="bg-[#7fc241] border-b-2 text-[#121212] border-l-2 border-r-2 border-[#8bd845] flex gap-2 h-12 items-center justify-center p-3 rounded-lg w-full"
                  >
                    <div className="text-base font-bold  text-center">
                      View My Full Course
                    </div>
                    <div className="flex h-[9px] items-center justify-center w-3">

                      <FontAwesomeIcon icon={faArrowRight} className='arrow-move' />


                    </div>
                  </button>

                  <div className="flex items-center justify-center">
                    <p className="text-xs font-medium italic text-[#83beff]">
                      ⚡ Saves you ~14 hours vs a generic course
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Analysis